import { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, Chip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { SalesInvoiceTemplateRenderer } from '../../sales/components/SalesInvoiceTemplateRenderer';

const TEMPLATE_LABELS = {
  standard: 'Template 1 – Standard A4',
  thermal: 'Template 2 – Thermal',
  template3: 'Template 3',
  template4: 'Template 4',
  template5: 'Template 5',
  template6: 'Template 6',
  template7: 'Template 7',
  template8: 'Template 8',
  template9: 'Template 9',
  template10: 'Template 10',
  template11: 'Template 11 – Tax Invoice Grid',
};

function buildSampleOrder({ currency = 'USD' } = {}) {
  const items = [
    {
      id: '1',
      product: { name: 'Wireless Mouse', sku: 'WM-100', brand: 'LogiPro' },
      quantity: 2,
      unit: 'pcs',
      unitPrice: 25,
      discount: 2,
      vatAmount: 2.4,
      total: 50 - 2 + 2.4,
    },
    {
      id: '2',
      product: { name: 'USB-C Cable 1m', sku: 'USBC-1M', brand: 'CableCo' },
      quantity: 3,
      unit: 'pcs',
      unitPrice: 8,
      discount: 0,
      vatAmount: 1.44,
      total: 24 + 1.44,
    },
    {
      id: '3',
      product: { name: 'Laptop Stand', sku: 'LS-200', brand: 'OfficeMax' },
      quantity: 1,
      unit: 'pcs',
      unitPrice: 35,
      discount: 5,
      vatAmount: 1.5,
      total: 35 - 5 + 1.5,
    },
  ];

  const subtotal = items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unitPrice || 0), 0);
  const discountTotal = items.reduce((s, it) => s + Number(it.discount || 0), 0);
  const vatTotal = items.reduce((s, it) => s + Number(it.vatAmount || 0), 0);
  const totalAmount = subtotal - discountTotal + vatTotal;
  const paidAmount = Math.round((totalAmount * 0.4) * 100) / 100;
  const balanceAmount = Math.round((totalAmount - paidAmount) * 100) / 100;

  return {
    id: 'sample',
    invoiceNumber: 'INV-000123',
    orderNumber: 'SO-000123',
    invoiceDate: new Date().toISOString(),
    salesType: 'credit',
    customer: {
      name: 'Sample Customer LLC',
      address: '123 Main Street\nCity, State\nCountry',
      trn: 'TRN-1234567890',
    },
    location: { name: 'Main Branch', code: 'MB-01' },
    items,
    subtotal,
    discountTotal,
    vatTotal,
    totalAmount,
    grandTotal: totalAmount,
    paidAmount,
    balanceAmount,
    notes: `Sample preview (${currency}). Replace with real data on actual print.`,
  };
}

export default function PrintTemplatePreview() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [searchParams] = useSearchParams();
  const doc = searchParams.get('doc') || 'sales';
  const { data: company, isLoading } = useGetCurrentCompanyQuery();

  const templateKey = TEMPLATE_LABELS[templateId] ? templateId : 'standard';
  const currency = company?.currency || 'USD';
  const itemFormat = company?.printItemNameFormat || 'name_sku';
  const dateFormat = company?.dateFormat || 'dd/MM/yyyy';
  const paperType = company?.printPaperType || 'a4';

  const sampleOrder = useMemo(() => buildSampleOrder({ currency }), [currency]);

  return (
    <Box>
      {paperType === 'a5' && <style>{`@page { size: A5; }`}</style>}

      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          '@media print': { display: 'none' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/settings')}>
            Back to Settings
          </Button>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Template Preview
          </Typography>
          <Chip
            size="small"
            color="info"
            label={`${TEMPLATE_LABELS[templateKey] || 'Template'} · ${doc === 'quotation' ? 'Quotation' : 'Sales Invoice'}`}
            sx={{ fontWeight: 600 }}
          />
        </Box>

        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()} sx={{ borderRadius: 2 }}>
          Print
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ p: 3 }}>
          <Typography>Loading...</Typography>
        </Box>
      ) : (
        <SalesInvoiceTemplateRenderer
          order={sampleOrder}
          company={company}
          templateKey={templateKey}
          currency={currency}
          itemFormat={itemFormat}
          dateFormat={dateFormat}
        />
      )}
    </Box>
  );
}

